// deskFacts — what an open binder's or shelf's rows say, and what came of
// planning and generating there (pen "Studio – the desk", "Document states",
// decisions 2 and 3). Pure. Every mapping from the server's facts onto what
// the desk shows lives here, each with an explicit default, so a state or
// refusal the server adds later is one more case.

export type { DeskBriefs, DeskNote, DocState, ReadFailure, ReadinessItem };
export {
    deskNotes,
    docStateOf,
    isQuiz,
    noteOfRefusal,
    plannedOf,
    readinessItems,
    targetName,
    toGenerate,
};

import type { ExportReadiness } from "../../app/ports/brief.js";
import type { GenerationJobSnapshot } from "../../app/ports/generation.js";
import type { ContainerCard, ContainerPart } from "../Studio/studioCards.js";

// A document's state in an open binder. An unknown state (a newer server)
// looks like Planned (neutral).
type DocState =
    | "generated"
    | "planned"
    | "writing"
    | "empty"
    | "removed"
    // A source: the original, never generated.
    | "source"
    | "unknown";

// From the library row's document state; an older server sends none, so the
// planned flag decides.
const docStateOf = (
    part: Pick<ContainerPart, "kind" | "planned" | "document">,
): DocState => {
    switch (part.kind) {
        case "source":
            return "source";
        case "material":
            switch (part.document?.state) {
                case "generated":
                case "planned":
                case "empty":
                case "removed":
                case "unknown":
                    return part.document.state;
                case "being_written":
                    return "writing";
                case undefined:
                    return part.planned ? "planned" : "generated";
                default:
                    return "unknown";
            }
        case "module":
        case "other":
            return "unknown";
        default:
            return "unknown";
    }
};

// A quiz: what it was planned as names it first (a planned page is no quiz,
// whatever template it waits with), else its template.
const isQuiz = (part: Pick<ContainerPart, "template" | "document">): boolean =>
    part.document?.kind === undefined
        ? part.template === "quiz" || part.template === "question"
        : part.document.kind === "quiz";

// What came of asking to generate (or to change a brief), shown at the
// document (or a container's foot) instead of an error message.
type DeskNote =
    // Asked; the answer is on its way.
    | { readonly type: "starting" }
    // The job reads the sources first (no facts were saved from them yet).
    | { readonly type: "reading" }
    // That read saved no fact, so nothing was written; Generate again.
    | { readonly type: "read_failed"; readonly reason: ReadFailure }
    // No source to write from (`generation_root_ungrounded`).
    | { readonly type: "no_sources" }
    // Its container is being written already.
    | { readonly type: "already_writing" }
    // Generated already: the brief stays as it is for now.
    | { readonly type: "brief_locked" }
    // Default: any other refusal, in its own words.
    | { readonly type: "failed"; readonly text: string };

// A refusal's leaf (`errorType`, absent when the failure was no refusal) and
// its display text onto a note.
const noteOfRefusal = (
    errorType: string | undefined,
    text: string,
): DeskNote => {
    switch (errorType) {
        case "generation_root_ungrounded":
            return { type: "no_sources" };
        case "generation_job_already_active":
            return { type: "already_writing" };
        case "brief_revise_after_generation_not_yet":
        case "generate_from_brief_not_planned":
            return { type: "brief_locked" };
        default:
            return { type: "failed", text };
    }
};

// Why a read saved no fact (`unknown`: a reason this client does not know).
type ReadFailure =
    | NonNullable<GenerationJobSnapshot["readFailure"]>
    | "unknown";

// What a running job says at its documents: only the read before writing
// (being written and written come with the library rows).
const jobNote = (job: GenerationJobSnapshot): DeskNote | null => {
    switch (job.phase) {
        case "reading":
            return { type: "reading" };
        case "read_failed":
            return {
                type: "read_failed",
                reason: job.readFailure ?? "unknown",
            };
        case "running":
        case "stopping":
        case "paused_budget":
        case "stopped":
        case "done":
        case "interrupted":
            return null;
        default:
            return null;
    }
};

// The notes by id: each watched job's note at its root and every part it
// writes, then the refusals and requests in flight (newer, so they win).
const deskNotes = (
    asked: ReadonlyMap<string, DeskNote>,
    jobs: Iterable<GenerationJobSnapshot>,
): ReadonlyMap<string, DeskNote> =>
    new Map([
        ...[...jobs].flatMap((job): [string, DeskNote][] => {
            const note = jobNote(job);
            return note === null
                ? []
                : [job.root, ...job.parts.map((p) => p.id)].map((id) => [
                      id,
                      note,
                  ]);
        }),
        ...asked,
    ]);

// What the desk is given for briefs: each open container's readiness, the
// notes by document (or container) id, and the commands.
type DeskBriefs = {
    readonly readiness: ReadonlyMap<string, ExportReadiness>;
    readonly notes: ReadonlyMap<string, DeskNote>;
    // The container's planned documents (`ids`), in one job.
    readonly onGenerateAll: (
        containerId: string,
        ids: readonly string[],
    ) => void;
};

// A packaging loss the readiness line words; any other is "other".
type Loss =
    | "lost_interactivity"
    | "lost_styling"
    | "lost_grading_logic"
    | "lost_audio_metadata"
    | "lost_objective_alignment"
    | "other";

const lossOf = (raw: string): Loss => {
    switch (raw) {
        case "lost_interactivity":
        case "lost_styling":
        case "lost_grading_logic":
        case "lost_audio_metadata":
        case "lost_objective_alignment":
            return raw;
        default:
            return "other";
    }
};

// One item on the readiness line before export.
type ReadinessItem =
    | { readonly type: "planned"; readonly count: number }
    | { readonly type: "empty"; readonly count: number }
    | { readonly type: "removed"; readonly count: number }
    | { readonly type: "loss"; readonly loss: Loss; readonly target: string }
    // Parts that move on the way (flattened, or outside a module).
    | {
          readonly type: "moved";
          readonly count: number;
          readonly target: string;
      }
    | { readonly type: "unavailable"; readonly target: string };

const readinessItems = (readiness: ExportReadiness): readonly ReadinessItem[] =>
    readiness.findings.flatMap((f): readonly ReadinessItem[] => {
        switch (f.type) {
            case "readiness_parts_planned":
                return [{ type: "planned", count: f.parts.length }];
            case "readiness_parts_empty":
                return [{ type: "empty", count: f.parts.length }];
            case "readiness_parts_removed":
                return [{ type: "removed", count: f.parts.length }];
            case "readiness_target_changes": {
                const target = targetName(f.target);
                const losses = [...new Set(f.losses.map(lossOf))];
                const moved = f.shape.filter((s) => s.type !== "unknown");
                const movedItems: readonly ReadinessItem[] =
                    moved.length === 0
                        ? []
                        : [{ type: "moved", count: moved.length, target }];
                return [
                    ...losses.map(
                        (loss): ReadinessItem => ({
                            type: "loss",
                            loss,
                            target,
                        }),
                    ),
                    ...movedItems,
                ];
            }
            case "readiness_target_unavailable":
                return [{ type: "unavailable", target: targetName(f.target) }];
            case "unknown":
                return [];
            default:
                return [];
        }
    });

// The planned documents "Generate it" writes.
const plannedOf = (readiness: ExportReadiness): readonly string[] =>
    readiness.findings.flatMap((f) =>
        f.type === "readiness_parts_planned" ? f.parts.map((p) => p.id) : [],
    );

// What "Generate it" writes: the planned documents not being written
// already (the readiness counts those as planned until they land).
const toGenerate = (
    readiness: ExportReadiness,
    card: ContainerCard,
): readonly string[] => {
    const writing = new Set(
        card.parts
            .flatMap((p) => [p, ...p.parts])
            .filter((p) => docStateOf(p) === "writing")
            .map((p) => p.id),
    );
    return plannedOf(readiness).filter((id) => !writing.has(id));
};

// A target format's name ("F_canvas" → "Canvas").
const targetName = (formatId: string): string => {
    switch (formatId) {
        case "F_canvas":
            return "Canvas";
        case "F_torus":
            return "Torus";
        default: {
            const bare = formatId.replace(/^F_/, "");
            return bare.charAt(0).toUpperCase() + bare.slice(1);
        }
    }
};
